import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import { getUserRole } from '../utils/rolePermissions';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import CommentModal from './CommentModal';
import { useSortableTable } from '../hooks/useSortableTable';
import { formatBlogBody } from '../utils/blogFormatting';
import '../styles/ToDoList.css';
import '../styles/ClientDashboard.css';

// Catches a client's approval/denial, or another operator's action, without
// requiring a manual page refresh. Longer than NotificationBell's interval
// since this fires 4 parallel requests per tick.
const POLL_INTERVAL_MS = 30000;

const ToDoList = ({ user, onSectionChange, isProxyMode = false }) => {
  const { brands, organizations, setBrand, setOrganization, selectedBrand, selectedOrganization, getBrandsForOrg } = useOrgBrand();
  const modal = useModal();
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('todo_list_active_tab') || 'todo';
  });
  const [ideasItems, setIdeasItems] = useState([]);
  const [contentDraftItems, setContentDraftItems] = useState([]);
  const [brandVoiceItems, setBrandVoiceItems] = useState([]);
  const [meetingItems, setMeetingItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [commentModalState, setCommentModalState] = useState({ open: false, itemId: null, itemType: null, docId: null, comments: [] });

  // Pagination state for history
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_ITEMS_PER_PAGE = 50;

  // Revision modal state
  const [revisionModal, setRevisionModal] = useState({
    open: false,
    type: null, // 'idea', 'content_draft', or 'brand_voice'
    itemId: null,
    docId: null, // for ideas
    ideaId: null // for ideas
  });
  const [revisionComment, setRevisionComment] = useState('');

  // View/Preview modal state for content drafts
  const [viewingDraft, setViewingDraft] = useState(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      fetchIdeasItems(),
      fetchContentDraftItems(),
      fetchBrandVoiceCompletions(),
      fetchMeetingItems()
    ]).finally(() => setLoading(false));

    // Silent background refresh - no setLoading, so a poll never flashes a
    // spinner over an already-populated list.
    const interval = setInterval(() => {
      Promise.all([
        fetchIdeasItems(),
        fetchContentDraftItems(),
        fetchBrandVoiceCompletions(),
        fetchMeetingItems()
      ]);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    localStorage.setItem('todo_list_active_tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (!user) return;
    if (activeTab === 'history') {
      setHistoryPage(1); // Reset to page 1 when switching to history
      fetchHistoryItems();
    }
  }, [activeTab, user]);

  const fetchIdeasItems = async () => {
    try {
      const response = await axios.get('/api/ideas', { withCredentials: true });
      const allIdeas = response.data.ideas || [];
      const isOperator = user?.is_operator || user?.role === 'super_admin';
      // Operators see ideas returned from client (revision requested or denied) and manually created ideas
      // Clients see ideas sent to them for review
      const agentStatuses = ['revision_requested', 'denied', 'pending_review'];
      const clientStatuses = ['sent_to_client'];
      const visibleStatuses = isOperator ? agentStatuses : clientStatuses;

      const flattenedIdeas = [];
      allIdeas.forEach(doc => {
        (doc.ideas || []).forEach(idea => {
          if (visibleStatuses.includes(idea.status)) {
            flattenedIdeas.push({
              ...idea,
              docId: doc._id,
              brandName: doc.brand?.name || doc.brand?.brand_name || 'Unknown Brand',
              brand_logo: doc.brand?.icon_url,
              brand_id: doc.brand?._id || doc.brand?.id,
              organizationName: doc.organization?.name || doc.organization?.organization_name || 'Unknown Organization',
              organization_id: doc.organization?._id || doc.organization?.id,
              generatedAt: doc.generated_at
            });
          }
        });
      });
      setIdeasItems(flattenedIdeas);
    } catch (error) {
      console.error('Error fetching ideas:', error);
    }
  };

  const fetchContentDraftItems = async () => {
    try {
      const response = await axios.get('/api/content-drafts', { withCredentials: true });
      const all = response.data.drafts || [];
      // Operators see drafts that need their review (pending or returned from client)
      // Clients see drafts sent to them
      const isOperator = user?.is_operator || user?.role === 'super_admin';
      const agentStatuses = ['pending_operator_review', 'revision_requested', 'denied', 'approved'];
      const filtered = isOperator
        ? all.filter(d => agentStatuses.includes(d.status))
        : all.filter(d => d.status === 'sent_to_client');

      // Ensure brand_id and organization_id are included for navigation
      const enriched = filtered.map(draft => ({
        ...draft,
        brand_id: draft.brand?._id || draft.brand?.id,
        organization_id: draft.organization?._id || draft.organization?.id
      }));

      setContentDraftItems(enriched);
    } catch (error) {
      console.error('Error fetching content drafts:', error);
    }
  };

  const fetchBrandVoiceCompletions = async () => {
    try {
      const isOperator = user?.is_operator || user?.role === 'super_admin';
      if (!isOperator) {
        // Only operators see brand voice completions
        setBrandVoiceItems([]);
        return;
      }

      const response = await axios.get('/api/brand-voice/completions', { withCredentials: true });
      const completions = response.data.completions || [];
      setBrandVoiceItems(completions);
    } catch (error) {
      console.error('Error fetching brand voice completions:', error);
      setBrandVoiceItems([]);
    }
  };

  const fetchMeetingItems = async () => {
    try {
      const response = await axios.get('/api/todo/meetings', { withCredentials: true });
      const meetings = response.data.meetings || [];
      setMeetingItems(meetings);
    } catch (error) {
      console.error('Error fetching meeting items:', error);
      setMeetingItems([]);
    }
  };

  const fetchHistoryItems = async () => {
    try {
      setLoadingHistory(true);
      const response = await axios.get('/api/todo-history', { withCredentials: true });
      setHistoryItems(response.data.history || []);
    } catch (error) {
      console.error('Error fetching history items:', error);
      modal.error('Failed to load history');
      setHistoryItems([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleItemNavigation = (item, targetSection) => {
    // Extract brand ID and org ID from different item types
    let brandId = null;
    let orgId = null;

    if (item.itemType === 'brand_voice') {
      brandId = item.brand_id;
      orgId = item.organization_id;
    } else if (item.itemType === 'idea') {
      brandId = item.brand_id;
      orgId = item.organization_id;
    } else if (item.itemType === 'content_draft') {
      brandId = item.brand_id || item.brand?._id || item.brand?.id;
      orgId = item.organization_id || item.organization?._id || item.organization?.id;
    } else {
      // Content items
      brandId = item.brand_id;
      orgId = item.organization_id;
    }

    // Find the brand and org in the context arrays
    const brand = brands.find(b => (b._id || b.id) === brandId);
    const org = organizations.find(o => (o._id || o.id) === orgId);

    let contextUpdated = false;

    // Switch organization first if needed
    if (org) {
      const currentOrgId = selectedOrganization?._id || selectedOrganization?.id;
      if (currentOrgId !== orgId) {
        setOrganization(org);
        contextUpdated = true;
      }
    }

    // Then switch brand if needed
    if (brand) {
      const currentBrandId = selectedBrand?._id || selectedBrand?.id;
      if (currentBrandId !== brandId) {
        setBrand(brand);
        contextUpdated = true;
      }
    }

    // Special handling for brand voice items
    if (item.itemType === 'brand_voice') {
      sessionStorage.setItem('open_brand_voice_tab', 'true');
      sessionStorage.setItem('pending_brand_voice_id', item.brand_id);
    }

    // Force the correct tab when navigating to review panels
    if (targetSection === 'operator-review-ideas') {
      sessionStorage.setItem('force_ideas_panel_tab', 'current');
    } else if (targetSection === 'review-content') {
      sessionStorage.setItem('force_review_content_tab', 'current');
    } else if (targetSection === 'schedule-posts') {
      localStorage.setItem('schedule_posts_active_tab', 'ready');
    }

    // Show success notification if context was updated
    if (contextUpdated && brand && org) {
      modal.toast(`Context updated: ${org.name || org.organization_name} > ${brand.name || brand.brand_name}`, 'success');
    } else if (contextUpdated && brand) {
      modal.toast(`Switched to ${brand.name || brand.brand_name}`, 'success');
    }

    // Navigate to target section
    if (onSectionChange && targetSection) {
      onSectionChange(targetSection);
    }
  };

  const handleBrandVoiceNavigation = (brandVoiceItem) => {
    handleItemNavigation(brandVoiceItem, 'assets');
  };

  const handleBrandVoiceDecision = async (brandVoiceItem, decision) => {
    const decisionText = decision === 'approved' ? 'approve' : decision === 'revision_requested' ? 'request revision for' : 'deny';
    const confirmResult = await modal.confirm(
      `Are you sure you want to ${decisionText} the Brand Voice completion for ${brandVoiceItem.brand_name}?`,
      'Confirm Action'
    );
    if (!confirmResult) return;

    try {
      const payload = { status: decision };
      if (brandVoiceItem.revisionComment) payload.comment = brandVoiceItem.revisionComment;
      await axios.put(
        `/api/brand-voice/completions/${brandVoiceItem._id}/status`,
        payload,
        { withCredentials: true }
      );
      const label = decision === 'approved' ? 'approved' : decision === 'revision_requested' ? 'revision requested' : 'denied';
      modal.success(`Brand Voice completion ${label} successfully!`);
      fetchBrandVoiceCompletions();
    } catch (error) {
      console.error('Error updating brand voice decision:', error);
      modal.error(error.response?.data?.error || 'Failed to update brand voice completion');
    }
  };

  const handleContentDecision = async (draftId, decision, comment = null) => {
    // If revision requested without comment, open modal
    if (decision === 'revision_requested' && !comment) {
      setRevisionModal({
        open: true,
        type: 'content_draft',
        itemId: draftId,
        docId: null,
        ideaId: null
      });
      setRevisionComment('');
      return;
    }

    const decisionText = decision === 'approved' ? 'approve' :
                        decision === 'denied' ? 'deny' : 'request revision for';
    const result = await modal.confirm(`Are you sure you want to ${decisionText} this content?`, 'Confirm Action');
    if (!result) return;

    try {
      const payload = {
        status: decision
      };

      // Add comment if provided (for revision requests)
      if (comment) {
        payload.comment = comment;
      }

      await axios.put(`/api/content-drafts/${draftId}/status`, payload, { withCredentials: true });
      fetchContentDraftItems();
    } catch (error) {
      console.error('Error updating content decision:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update content';
      modal.error(errorMessage);

      // If assignment error, close modals and refresh all lists
      if (errorMessage.includes('no longer assigned')) {
        setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null });
        setCommentModalState({ open: false, itemId: null, itemType: null, docId: null, comments: [] });
        fetchIdeasItems();
        fetchContentDraftItems();
      }
    }
  };

  const handleIdeaDecision = async (docId, ideaId, decision, comment = null) => {
    // If revision requested without comment, open modal
    if (decision === 'revision_requested' && !comment) {
      setRevisionModal({
        open: true,
        type: 'idea',
        itemId: null,
        docId: docId,
        ideaId: ideaId
      });
      setRevisionComment('');
      return;
    }

    const decisionText = decision === 'approved' ? 'approve' :
                        decision === 'denied' ? 'deny' : 'request revision for';

    const confirmResult = await modal.confirm(`Are you sure you want to ${decisionText} this idea?`, 'Confirm Action');
    if (!confirmResult) {
      return;
    }

    try {
      const payload = {
        status: decision
      };

      // Add comment if provided (for revision requests)
      if (comment) {
        payload.comment = comment;
      }

      await axios.put(`/api/ideas/${docId}/idea/${ideaId}/status`, payload, { withCredentials: true });

      modal.success(`Idea ${decision === 'approved' ? 'approved' : decision === 'denied' ? 'denied' : 'revision requested'} successfully!`);
      fetchIdeasItems();
    } catch (error) {
      console.error('Error updating idea decision:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update idea';
      modal.error(errorMessage);

      // If assignment error, close modals and refresh all lists
      if (errorMessage.includes('no longer assigned')) {
        setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null });
        setCommentModalState({ open: false, itemId: null, itemType: null, docId: null, comments: [] });
        fetchIdeasItems();
        fetchContentDraftItems();
      }
    }
  };

  const handleRevisionSubmit = async () => {
    if (!revisionComment.trim()) {
      modal.warning('Please enter a comment explaining what needs revision');
      return;
    }

    if (revisionModal.type === 'content_draft') {
      await handleContentDecision(revisionModal.itemId, 'revision_requested', revisionComment);
    } else if (revisionModal.type === 'idea') {
      await handleIdeaDecision(revisionModal.docId, revisionModal.ideaId, 'revision_requested', revisionComment);
    } else if (revisionModal.type === 'brand_voice') {
      const item = brandVoiceItems.find(i => i._id === revisionModal.itemId);
      if (item) {
        await handleBrandVoiceDecision({ ...item, revisionComment }, 'revision_requested');
      }
    }

    // Close modal and reset
    setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null });
    setRevisionComment('');
  };

  const formatDate = (date) => {
    if (!date) return 'Not scheduled';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCommentTimestamp = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${month}/${day} ${hours}:${minutesStr} ${ampm}`;
  };

  const getBrandDisplay = (item) => {
    // Handle both content items (brand_name, brand_logo) and idea items (brandName, brand_logo)
    const brandName = item.brandName || item.brand_name;
    const brandLogo = item.brand_logo;

    if (brandLogo) {
      return <BrandIcon src={brandLogo} alt={brandName || 'Brand'} className="brand-logo" />;
    }
    if (brandName) {
      return (
        <div className="brand-placeholder">
          <span>{brandName.charAt(0).toUpperCase()}</span>
        </div>
      );
    }
    return (
      <div className="brand-placeholder">
        <Icon name="feeds" size="1.5rem" />
      </div>
    );
  };

  const capitalizeTag = (tag) => {
    if (!tag) return '';
    return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
  };

  const getTagsDisplay = (item) => {
    let tags = [];
    try {
      const content = JSON.parse(item.text_content);
      tags = content.tags || [];
    } catch (e) {
      tags = item.content_type ? [item.content_type] : [];
    }

    const capitalizedTags = tags.map(tag => capitalizeTag(tag));
    const text = capitalizedTags.length > 0 ? capitalizedTags.join(', ') : (item.content_type ? capitalizeTag(item.content_type) : '');
    return <span className="tags-text">{text}</span>;
  };

  const getContentTypeBadge = (type) => {
    const typeConfig = {
      'social': { label: 'Social', color: '#3B82F6' },
      'blog': { label: 'Blog', color: '#10B981' },
      'video': { label: 'Video', color: '#EF4444' },
      'podcast': { label: 'Podcast', color: '#8B5CF6' },
      'newsletter': { label: 'Newsletter', color: '#F59E0B' }
    };
    const config = typeConfig[type] || { label: type, color: '#6B7280' };
    return (
      <span className="content-type-badge" style={{ backgroundColor: config.color }}>
        {config.label}
      </span>
    );
  };

  const getIdeaTagsDisplay = (idea) => {
    const contentType = idea.content_type ? capitalizeTag(idea.content_type) : '';
    return <span className="tags-text">{contentType}</span>;
  };

  const totalPendingItems = ideasItems.length + contentDraftItems.length + brandVoiceItems.length;

  // Combine ideas, content drafts, brand voice completions, and meetings into one unified list
  // Show either todo items or history items based on active tab
  const baseItems = activeTab === 'todo' ? [
    ...ideasItems.map(idea => ({ ...idea, itemType: 'idea' })),
    ...contentDraftItems.map(draft => ({
      ...draft,
      itemType: 'content_draft',
      brandName: draft.brand?.name || 'Unknown Brand',
      brand_logo: draft.brand?.icon_url,
      brand_id: draft.brand_id || draft.brand?._id || draft.brand?.id,
      organization_id: draft.organization_id || draft.organization?._id || draft.organization?.id
    })),
    ...brandVoiceItems.map(completion => ({
      ...completion,
      itemType: 'brand_voice',
      showGreenArrow: true
    })),
    ...meetingItems.map(meeting => ({
      ...meeting,
      itemType: 'meeting',
      brandName: meeting.brand_name || 'Unknown Brand',
      brand_logo: meeting.brand_logo,
      brand_id: meeting.brand_id,
      organization_id: meeting.organization_id,
      organizationName: meeting.organization_name
    }))
  ] : historyItems;

  const rawItems = baseItems.map(item => ({
    ...item,
    _sortBrand: item.brandName || item.brand_name || item.brand?.name || '',
    _sortDate: activeTab === 'history'
      ? // For history: use action date (most recent action first)
        (item.responded_at || item.archived_at || item.sent_to_client_at || item.client_decision_at || item.updated_at || item.created_at)
      : // For pending: use when it was sent/assigned to user (most recent first)
        item.itemType === 'idea'
          ? (item.sent_to_client_at || item.generatedAt)
          : item.itemType === 'content_draft'
            ? (item.sent_to_client_at || item.created_at)
            : item.itemType === 'brand_voice'
              ? item.completed_at
              : item.itemType === 'meeting'
                ? item.scheduled_start
                : (item.sent_to_client_at || item.scheduled_date || item.created_at),
  })).sort((a, b) => {
    // Default sort: newest first (by _sortDate descending)
    const dateA = new Date(a._sortDate || 0);
    const dateB = new Date(b._sortDate || 0);
    return dateB - dateA;
  });

  const { sortedData: sortedItems, requestSort: requestTodoSort, getSortClass: getTodoSortClass } = useSortableTable(rawItems);

  // Apply pagination for history tab
  const allItems = activeTab === 'history'
    ? sortedItems.slice((historyPage - 1) * HISTORY_ITEMS_PER_PAGE, historyPage * HISTORY_ITEMS_PER_PAGE)
    : sortedItems;

  const totalPages = activeTab === 'history' ? Math.ceil(sortedItems.length / HISTORY_ITEMS_PER_PAGE) : 1;
  const startIndex = activeTab === 'history' ? (historyPage - 1) * HISTORY_ITEMS_PER_PAGE : 0;
  const endIndex = activeTab === 'history' ? Math.min(startIndex + HISTORY_ITEMS_PER_PAGE, sortedItems.length) : sortedItems.length;

  const renderContentPreviewModal = () => {
    if (!viewingDraft) return null;
    const draft = viewingDraft;
    const brandName = draft.brand?.name || 'Brand';
    const brandLogo = draft.brand?.icon_url;
    const isSocial = draft.content_type === 'social';
    const isBlog = draft.content_type === 'blog';
    const dateStr = formatDate(draft.client_decision_at || draft.created_at);

    const extractHashtags = (text) => (text || '').match(/#\w+/g) || [];
    const hashtags = [...new Set([
      ...extractHashtags(draft.production_notes || ''),
      ...extractHashtags(draft.body || '')
    ])];
    const cleanBody = (draft.body || '').replace(/#\w+/g, '').trim();

    const getContentTypeBadge = (type) => {
      const typeConfig = {
        'social': { label: 'Social', color: '#3B82F6' },
        'blog': { label: 'Blog', color: '#10B981' },
        'video': { label: 'Video', color: '#EF4444' },
        'podcast': { label: 'Podcast', color: '#8B5CF6' },
        'newsletter': { label: 'Newsletter', color: '#F59E0B' }
      };
      const config = typeConfig[type] || { label: type, color: '#6B7280' };
      return (
        <span className="content-type-badge" style={{ backgroundColor: config.color }}>
          {config.label}
        </span>
      );
    };

    return (
      <div className="mock-modal-overlay" onClick={() => setViewingDraft(null)}>
        <div className="mock-modal" onClick={(e) => e.stopPropagation()}>
          <div className="mock-modal-header">
            <h3>Content Preview</h3>
            <button className="modal-close-btn" onClick={() => setViewingDraft(null)} aria-label="Close" />
          </div>

          <div className="mock-modal-body">
            {isSocial ? (
              <div className="mock-social-post">
                <div className="mock-post-header">
                  <div className="mock-author-info">
                    {brandLogo ? (
                      <BrandIcon src={brandLogo} alt={brandName} className="mock-brand-avatar" />
                    ) : (
                      <div className="mock-brand-initial">{brandName.charAt(0).toUpperCase()}</div>
                    )}
                    <div>
                      <div className="mock-brand-name">{brandName}</div>
                      <div className="mock-post-meta">{dateStr} · <Icon name="link" size="0.9rem" /></div>
                    </div>
                  </div>
                  <span className="mock-type-tag mock-type-social">SOCIAL</span>
                </div>
                <div className="mock-post-body">
                  {formatBlogBody(cleanBody || draft.body || '').map(block => (
                    block.type === 'heading'
                      ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                      : <p key={block.key} className="mock-post-paragraph">{block.text}</p>
                  ))}
                </div>
                {hashtags.length > 0 && (
                  <div className="mock-hashtags">
                    {hashtags.map((h, i) => <span key={i} className="mock-hashtag">{h} </span>)}
                  </div>
                )}
                {draft.media_url && (
                  <div className="mock-media-wrapper">
                    <img src={draft.media_url} alt="Content media" className="mock-post-image" />
                  </div>
                )}
                <div className="mock-social-actions">
                  <span className="mock-social-btn" style={{ color: '#E1306C' }}><Icon name="heart" size="0.9rem" /> Like</span>
                  <span className="mock-social-btn"><Icon name="comment-empty" size="0.9rem" /> Comment</span>
                  <span className="mock-social-btn"><Icon name="share" size="0.9rem" /> Share</span>
                  <span className="mock-social-btn"><Icon name="paper-plane-empty" size="0.9rem" /> Send</span>
                </div>
              </div>
            ) : isBlog ? (
              <div className="mock-blog-post">
                <div className="mock-blog-url-bar">
                  <span className="mock-blog-url">
                    {draft.call_to_action || `https://blog.example.com/${(draft.headline || draft.title || 'post').toLowerCase().replace(/\s+/g, '-')}`}
                  </span>
                  <span className="mock-type-tag mock-type-blog">BLOG</span>
                </div>
                {draft.media_url && (
                  <div className="mock-blog-image-wrapper">
                    <img src={draft.media_url} alt="Blog header" className="mock-blog-image" />
                  </div>
                )}
                {draft.headline && <h2 className="mock-blog-title">{draft.headline}</h2>}
                <div className="mock-blog-date">{dateStr}</div>
                <div className="mock-blog-body">
                  {formatBlogBody(draft.body).map(block => (
                    block.type === 'heading'
                      ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                      : <p key={block.key} className="mock-blog-paragraph">{block.text}</p>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mock-generic-post">
                <div className="mock-post-header">
                  <div className="mock-author-info">
                    {brandLogo ? (
                      <BrandIcon src={brandLogo} alt={brandName} className="mock-brand-avatar" />
                    ) : (
                      <div className="mock-brand-initial">{brandName.charAt(0).toUpperCase()}</div>
                    )}
                    <div>
                      <div className="mock-brand-name">{brandName}</div>
                      <div className="mock-post-meta">{dateStr}</div>
                    </div>
                  </div>
                  <span className="mock-type-tag" style={{ backgroundColor: getContentTypeBadge(draft.content_type).props.style.backgroundColor }}>
                    {(draft.content_type || 'CONTENT').toUpperCase()}
                  </span>
                </div>
                {draft.headline && <div className="mock-post-title">{draft.headline}</div>}
                <div className="mock-post-body">
                  {formatBlogBody(draft.body).map(block => (
                    block.type === 'heading'
                      ? <h4 key={block.key} className="mock-blog-subheading">{block.text}</h4>
                      : <p key={block.key} className="mock-post-paragraph">{block.text}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading && activeTab === 'todo') {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading to-do list...</p>
      </div>
    );
  }

  return (
    <div className="todo-list-container">
      <div className="todo-content-wrapper">
        {/* Tab Navigation */}
        <div className="todo-tabs-container">
          <button
            className={`todo-tab ${activeTab === 'todo' ? 'active' : ''}`}
            onClick={() => setActiveTab('todo')}
          >
            Pending
            {totalPendingItems > 0 && (
              <span className="todo-tab-badge">{totalPendingItems}</span>
            )}
          </button>
          <button
            className={`todo-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        {/* Pagination Info for History */}
        {activeTab === 'history' && rawItems.length > 0 && (
          <div className="todo-pagination-info">
            Showing {startIndex + 1}-{endIndex} of {rawItems.length} items
          </div>
        )}

        {/* Unified Table for All Items */}
        <div className="todo-table-card">
          <table className="todo-table">
            <thead>
              <tr>
                <th className={getTodoSortClass('itemType')} onClick={() => requestTodoSort('itemType')}>Type</th>
                <th className={getTodoSortClass('_sortBrand')} onClick={() => requestTodoSort('_sortBrand')}>Brand</th>
                <th className={getTodoSortClass('title')} onClick={() => requestTodoSort('title')}>Details</th>
                <th className={getTodoSortClass('_sortDate')} onClick={() => requestTodoSort('_sortDate')}>
                  {activeTab === 'history' ? 'Action Date' : 'Received'}
                </th>
                <th>Actions</th>
                <th>Comments</th>
                <th>Tags</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {allItems.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan="8" className="empty-cell">
                    <span>{activeTab === 'todo' ? 'No items pending review' : 'No history items'}</span>
                  </td>
                </tr>
              ) : (
                allItems.map((item) => {
                  const isIdea = item.itemType === 'idea';
                  const isContentDraft = item.itemType === 'content_draft';
                  const isBrandVoice = item.itemType === 'brand_voice';
                  const isMeeting = item.itemType === 'meeting';
                  const itemKey = isIdea ? `${item.docId}-${item.id}` : item._id;

                  const isAgentUser = user?.is_operator || user?.is_admin || user?.role === 'super_admin';
                  const agentContentDraft = isContentDraft && isAgentUser;

                  return (
                    <tr key={itemKey}>
                      <td className="type-badge-cell">
                        {isIdea ? (
                          <span className="type-label"><Icon name="lightbulb" size="1rem" /> Idea</span>
                        ) : isContentDraft ? (
                          item.status === 'approved' ? (
                            <span className="type-label"><Icon name="calendar" size="1rem" /> Ready To Schedule</span>
                          ) : (
                            <span className="type-label"><Icon name="pencil" size="1rem" /> Content</span>
                          )
                        ) : isBrandVoice ? (
                          <span className="type-label"><Icon name="star-empty" size="1rem" /> Brand Voice</span>
                        ) : isMeeting ? (
                          <span className="type-label"><Icon name="video" size="1rem" /> Meeting</span>
                        ) : null}
                      </td>
                      <td className="brand-cell">
                        {getBrandDisplay(item)}
                      </td>
                      <td className="post-cell">
                        <div className="post-content">
                          {isIdea ? (
                            <>
                              <div className="todo-idea-title-row">
                                <strong>{item.title}</strong>
                                {item.status === 'revision_requested' && (
                                  <span className="todo-status-badge revision">Revision Requested</span>
                                )}
                                {item.status === 'denied' && (
                                  <span className="todo-status-badge denied">Client Denied</span>
                                )}
                              </div>
                              <div className="idea-synopsis">
                                {item.synopsis || item.description}
                              </div>
                            </>
                          ) : isContentDraft ? (
                            <>
                              <div className="todo-idea-title-row">
                                <strong>{isAgentUser ? (item.title || item.headline) : (item.headline || item.title)}</strong>
                                {item.status === 'approved' && (
                                  <span className="todo-status-badge approved">Approved</span>
                                )}
                              </div>
                              <div className="idea-synopsis">
                                {item.body
                                  ? item.body.length > 120 ? item.body.substring(0, 120) + '...' : item.body
                                  : item.synopsis || 'No preview available'}
                              </div>
                            </>
                          ) : isBrandVoice ? (
                            <div>
                              <strong>{item.brand_name}</strong> - Brand Voice Designer
                            </div>
                          ) : isMeeting ? (
                            <>
                              <div>
                                <strong>{item.title}</strong>
                              </div>
                              <div className="idea-synopsis">
                                {item.description || 'No description'}
                              </div>
                            </>
                          ) : null}
                        </div>
                      </td>
                      <td className="date-cell">
                        {formatDate(item._sortDate)}
                      </td>
                      <td className="approve-cell">
                        {activeTab === 'history' ? (
                          <div className="history-status">
                            {item.status === 'approved' && (
                              <span className="history-badge approved">Approved</span>
                            )}
                            {(item.status === 'denied' || item.status === 'rejected') && (
                              <span className="history-badge denied">Denied</span>
                            )}
                            {item.status === 'revision_requested' && (
                              <span className="history-badge revision">Revision Requested</span>
                            )}
                            {item.status === 'attended' && (
                              <span className="history-badge approved">Attended</span>
                            )}
                            {item.status === 'missed' && (
                              <span className="history-badge denied">Missed</span>
                            )}
                            {item.status === 'canceled' && (
                              <span className="history-badge revision">Canceled</span>
                            )}
                            {item.status === 'sent_to_client' && (
                              <span className="history-badge approved">Sent to Client</span>
                            )}
                            {item.status === 'reviewed' && (
                              <span className="history-badge approved">Reviewed</span>
                            )}
                            {item.status === 'dismissed' && (
                              <span className="history-badge revision">Dismissed</span>
                            )}
                            {item.status === 'approved_internal' && (
                              <span className="history-badge approved">Approved (Internal)</span>
                            )}
                            {item.status === 'rejected_internal' && (
                              <span className="history-badge denied">Discarded</span>
                            )}
                            {item.status === 'scheduled' && (
                              <span className="history-badge approved">Scheduled</span>
                            )}
                            {item.status === 'discarded' && (
                              <span className="history-badge denied">Discarded</span>
                            )}
                            {item.status === 'archived' && (
                              <span className="history-badge archived">Archived</span>
                            )}
                          </div>
                        ) : isBrandVoice ? (
                          <button
                            className="review-nav-btn"
                            onClick={() => handleBrandVoiceNavigation(item)}
                            title="Review Brand Voice"
                          >
                            <Icon name="go-to-link" size="1rem" />
                            Review
                          </button>
                        ) : isMeeting ? (
                          <button
                            className="review-nav-btn"
                            onClick={() => window.open(`/join-meeting/${item._id}`, '_blank')}
                            title="Join Meeting"
                          >
                            <Icon name="video" size="1rem" />
                            Join
                          </button>
                        ) : (isIdea && isAgentUser) || agentContentDraft ? (
                          <button
                            className="review-nav-btn"
                            onClick={() => handleItemNavigation(
                              item,
                              isIdea ? 'operator-review-ideas' : (item.status === 'approved' ? 'schedule-posts' : 'review-content')
                            )}
                            title={isIdea ? 'Go to Review Ideas' : (item.status === 'approved' ? 'Go to Schedule Posts' : 'Go to Review Content')}
                          >
                            <Icon name="go-to-link" size="1rem" />
                            {!isIdea && item.status === 'approved' ? 'Schedule' : 'Review'}
                          </button>
                        ) : isIdea ? (
                          <div className="approve-buttons">
                            <button
                              className="approve-btn approve"
                              onClick={() => handleIdeaDecision(item.docId, item.id, 'approved')}
                              title="Approve"
                            >
                              <Icon name="ok" size="1.25rem" />
                            </button>
                            <button
                              className="approve-btn flag"
                              onClick={() => handleIdeaDecision(item.docId, item.id, 'revision_requested')}
                              title="Flag for revision"
                            >
                              <Icon name="flag" size="1.25rem" />
                            </button>
                            <button
                              className="approve-btn deny"
                              onClick={() => handleIdeaDecision(item.docId, item.id, 'denied')}
                              title="Deny"
                            >
                              <Icon name="cancel" size="1.25rem" />
                            </button>
                          </div>
                        ) : isContentDraft ? (
                          <div className="approve-buttons">
                            <button
                              className="approve-btn approve"
                              onClick={() => handleContentDecision(item._id, 'approved')}
                              title="Approve"
                            >
                              <Icon name="ok" size="1.25rem" />
                            </button>
                            <button
                              className="approve-btn flag"
                              onClick={() => handleContentDecision(item._id, 'revision_requested')}
                              title="Flag for revision"
                            >
                              <Icon name="flag" size="1.25rem" />
                            </button>
                            <button
                              className="approve-btn deny"
                              onClick={() => handleContentDecision(item._id, 'denied')}
                              title="Deny"
                            >
                              <Icon name="cancel" size="1.25rem" />
                            </button>
                          </div>
                        ) : null}
                      </td>
                      <td className="comment-cell">
                        {isBrandVoice ? null : activeTab === 'history' ? (
                          <button
                            className="comment-icon-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const itemType = isIdea ? 'idea' : isContentDraft ? 'content-draft' : 'meeting';
                              setCommentModalState({
                                open: true,
                                itemId: isIdea ? item.id : item._id,
                                itemType: itemType,
                                docId: isIdea ? item.docId : null,
                                comments: item.comments || []
                              });
                            }}
                            title="View comments"
                            type="button"
                          >
                            <Icon name="comment-empty" size="2rem" />
                            {((isIdea || isContentDraft || isMeeting) && item.comments && item.comments.length > 0) ||
                              (!isIdea && !isContentDraft && !isMeeting && item.comments_count > 0) ? (
                              <span className="comment-count-badge">
                                {(isIdea || isContentDraft || isMeeting) ? item.comments.length : item.comments_count}
                              </span>
                            ) : null}
                          </button>
                        ) : (
                          <button
                            className="comment-icon-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const itemType = isIdea ? 'idea' : isContentDraft ? 'content-draft' : 'meeting';
                              setCommentModalState({
                                open: true,
                                itemId: isIdea ? item.id : item._id,
                                itemType: itemType,
                                docId: isIdea ? item.docId : null,
                                comments: item.comments || []
                              });
                            }}
                            title="Add comment"
                            type="button"
                          >
                            <Icon name="comment-empty" size="2rem" />
                            {((isIdea || isContentDraft || isMeeting) && item.comments && item.comments.length > 0) ||
                              (!isIdea && !isContentDraft && !isMeeting && item.comments_count > 0) ? (
                              <span className="comment-count-badge">
                                {(isIdea || isContentDraft || isMeeting) ? item.comments.length : item.comments_count}
                              </span>
                            ) : null}
                          </button>
                        )}
                      </td>
                      <td className="tags-cell">
                        {isBrandVoice ? null : (isIdea ? getIdeaTagsDisplay(item) : getTagsDisplay(item))}
                      </td>
                      <td className="view-cell">
                        {isBrandVoice ? (
                          <span className="view-disabled" title="Brand voice cannot be viewed here">
                            <Icon name="go-to-link" size="1.75rem" />
                          </span>
                        ) : isContentDraft ? (
                          <button
                            className="view-link"
                            onClick={() => setViewingDraft(item)}
                            title="View content preview"
                          >
                            <Icon name="go-to-link" size="1.75rem" />
                          </button>
                        ) : !isIdea && item.live_url ? (
                          <a
                            href={item.live_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="view-link"
                            title="View live post"
                          >
                            <Icon name="go-to-link" size="1.75rem" />
                          </a>
                        ) : (
                          <span className="view-disabled" title={isIdea ? 'Ideas cannot be viewed' : 'Not published yet'}>
                            <Icon name="go-to-link" size="1.75rem" />
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Pagination Controls for History */}
          {activeTab === 'history' && totalPages > 1 && (
            <div className="todo-pagination-controls">
              <button
                className="todo-pagination-btn"
                onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                disabled={historyPage === 1}
              >
                <Icon name="arrow-left" size="1rem" />
                Previous
              </button>
              <div className="todo-pagination-numbers">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (historyPage <= 3) {
                    pageNum = i + 1;
                  } else if (historyPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = historyPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`todo-pagination-number ${historyPage === pageNum ? 'active' : ''}`}
                      onClick={() => setHistoryPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                className="todo-pagination-btn"
                onClick={() => setHistoryPage(prev => Math.min(totalPages, prev + 1))}
                disabled={historyPage === totalPages}
              >
                Next
                <Icon name="arrow-right" size="1rem" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Revision Comment Modal */}
      {revisionModal.open && (
        <div className="revision-modal-overlay" onClick={() => setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null })}>
          <div className="revision-modal" onClick={(e) => e.stopPropagation()}>
            <div className="revision-modal-header">
              <h3>Request Revision</h3>
              <button
                className="modal-close-btn"
                onClick={() => setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null })}
                aria-label="Close"
              />
            </div>
            <div className="revision-modal-body">
              <p>Please explain what needs to be revised:</p>
              <textarea
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
                placeholder="Enter your revision notes here..."
                rows={5}
                autoFocus
              />
            </div>
            <div className="revision-modal-footer">
              <button
                className="cancel-modal-btn"
                onClick={() => setRevisionModal({ open: false, type: null, itemId: null, docId: null, ideaId: null })}
              >
                Cancel
              </button>
              <button
                className="submit-modal-btn"
                onClick={handleRevisionSubmit}
              >
                Submit Revision Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Preview Modal */}
      {renderContentPreviewModal()}

      {/* Comment Modal */}
      <CommentModal
        isOpen={commentModalState.open}
        onClose={() => setCommentModalState({ open: false, itemId: null, itemType: null, docId: null, comments: [] })}
        itemId={commentModalState.itemId}
        itemType={commentModalState.itemType}
        docId={commentModalState.docId}
        comments={commentModalState.comments}
        user={user}
        onCommentAdded={(updatedComments) => {
          if (commentModalState.itemType === 'idea') {
            setIdeasItems(prev => prev.map(item =>
              item.id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          } else if (commentModalState.itemType === 'content-draft') {
            setContentDraftItems(prev => prev.map(item =>
              item._id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          } else if (commentModalState.itemType === 'meeting') {
            setMeetingItems(prev => prev.map(item =>
              item._id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          }
        }}
        onCommentDeleted={(updatedComments) => {
          if (commentModalState.itemType === 'idea') {
            setIdeasItems(prev => prev.map(item =>
              item.id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          } else if (commentModalState.itemType === 'content-draft') {
            setContentDraftItems(prev => prev.map(item =>
              item._id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          } else if (commentModalState.itemType === 'meeting') {
            setMeetingItems(prev => prev.map(item =>
              item._id === commentModalState.itemId ? { ...item, comments: updatedComments } : item
            ));
          }
        }}
        isProxyMode={isProxyMode}
      />

    </div>
  );
};

export default ToDoList;
